import { useCallback } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  getWorkingProjectSessions,
  selectIsLoadingWorkingSession,
  selectSeriesRequired,
  selectWorkingProjectType,
  selectWorkingSession,
} from '@/store/slices/project/selectors';
import { Case, SwitchCase } from '@ui/switch-case/switch-case';
import EditSessionImage from '../../components/edit-session-image/edit-session-image';
import { EventType } from '@/types/projects.types';
import EditProjectCategory from '../../components/edit-project-category/edit-project-category';
import EditProjectSeries from '../../components/edit-project-series/edit-project-series';
import EditProjectTitle from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/edit-project-title/edit-project-title';
import Container from '@ui/container/container';
import SessionStreamInfo from '../../components/session-stream-info/session-stream-info';
import EditSessionDescription from '../../components/edit-session-description/edit-session-description';
import EditTags from '../../components/edit-tags/edit-tags';
import EditSessionLanguage from '../../components/edit-session-language/edit-session-language';
import EventStats from '../../components/stats-row/event-stats/event-stats';
import TownhallStats from '../../components/stats-row/townhall-stats/townhall-stats';
import WebinarStats from '../../components/stats-row/webinar-stats/webinar-stats';
import { EBroadcastTypes } from '@/types/session.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import useResourcePool, { ResourcePoolContext } from '@/utils/greenroom/use-resource-pool';
import GreenroomShotsRow from '../../components/greenroom-shots-row/greenroom-shots-row';
import VideoSettings from '../../components/video-settings/video-settings';
import ActivityIndicator from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/activity-indicator/activity-indicator';
import useLoadSession from '@/connection/hooks/use-load-session';
import Chip from '@styles/design-system/components/chips/chip';
import { useLoadCurrentSeries } from '../../components/edit-project-series/use-load-current-series';
import { selectCurrentSeries, selectTopDrawerOpen } from '@/store/slices/series/selectors';
import { closeTopDrawer, openTopDrawer } from '@/store/slices/series';
import CastAndCrewSelectWithInvites from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/cast-and-crew/cast-and-crew-select-with-invites/cast-and-crew-select-with-invites';
import Engagement from '../../components/engagement-row/engagement';
import EditSessionCaptions from '../../components/edit-session-captions/edit-session-captions';
import StylepacksRow from '../../components/stylepacks-row/stylepacks-row';
import ProjectImageSkeletonLoader from '@ui/skeleton-loader/custom/project-image-skeleton-loader';

const OverviewSingleSession = () => {
  const projectType = useAppSelector(selectWorkingProjectType);
  const sessions = useAppSelector(getWorkingProjectSessions);
  const session = useAppSelector(selectWorkingSession);
  const resourcePool = useResourcePool();
  const seriesRequired = useAppSelector(selectSeriesRequired);
  const currentSeries = useAppSelector(selectCurrentSeries);
  const topDrawerOpen = useAppSelector(selectTopDrawerOpen);
  const isLoadingWorkingSession = useAppSelector(selectIsLoadingWorkingSession);
  const dispatch = useAppDispatch();

  const params = useParams();

  const projectUuid = params?.projectUuid;
  const language = params?.language || 'en';

  const isSingleSession = sessions?.length === 1;
  const sessionUuid = sessions?.[0]?.uuid;
  const sessionId = sessions?.[0]?.session;

  useLoadSession(sessionId);
  useLoadCurrentSeries();

  const toggleTopDrawer = useCallback(() => {
    if (!topDrawerOpen) {
      dispatch(openTopDrawer());
    } else {
      dispatch(closeTopDrawer());
    }
  }, [topDrawerOpen, dispatch]);

  const getTitlePlaceholder = useCallback(() => {
    if (projectType === EventType.Webinar) {
      return 'Give your webinar a name';
    }
    return 'Give your project a name';
  }, [projectType]);

  if (!projectUuid) {
    return <Navigate to="/choose" />;
  }
  if (!isSingleSession) {
    <Navigate to={`/project/edit/${projectUuid}/${language}/${sessionUuid}`} />;
  }

  return (
    <div data-component="OverviewSingleSession">
      <div className="flex-column gap-10">
        <Container
          padding="0 var(--main-content-horizontal-padding) 10px var(--main-content-horizontal-padding)"
          className="flex flex-1 jc-space-between"
        >
          <Container className="flex flex-1">
            {!!currentSeries && !!session && (
              <Container className="text-nowrap margin-r-8 cursor-pointer" onClick={toggleTopDrawer}>
                <Chip
                  size={32}
                  label={currentSeries.title}
                  selectProps={{
                    style: {
                      padding: '4px 12px',
                      background: 'var(--gray-800)',
                      borderColor: 'var(--gray-700)',
                    },
                  }}
                />
              </Container>
            )}
            <EditProjectTitle
              syncSessionUuid={sessionUuid}
              sessionDefaultLanguage={session?.default_language}
              placeholder={getTitlePlaceholder()}
            />
          </Container>
          <Container
            className="flex"
            skLoader={{
              height: 32,
              width: 120,
              radius: 8,
              loading: isLoadingWorkingSession,
              design: 'GlobalCont1',
            }}
          >
            <ActivityIndicator />
          </Container>
        </Container>
        <Container
          padding="0 var(--main-content-horizontal-padding)"
          className="flex jc-flex-start ai-flex-start gap-24 margin-b-16"
        >
          <Container width="392px" className="flex-column gap-16">
            {/* syncProjectImage = if single session we need to also update the project image when updating the session image */}

            <Container style={{ position: 'relative' }}>
              <EditSessionImage />
              <ProjectImageSkeletonLoader loading={isLoadingWorkingSession} />
            </Container>

            <Container
              skLoader={{
                height: 172,
                width: 392,
                radius: 8,
                loading: isLoadingWorkingSession,
                design: 'GlobalCont1',
              }}
            >
              <SwitchCase input={projectType}>
                <Case value={EventType.Event}>
                  <EventStats />
                </Case>
                <Case value={EventType.Webinar}>
                  <WebinarStats />
                </Case>
                <Case value={[EventType.Allhands, EventType.Townhall]}>
                  <TownhallStats />
                </Case>
              </SwitchCase>
            </Container>
          </Container>

          <Container
            className="flex-column flex-1"
            skLoader={{
              height: 462,
              width: 576,
              radius: 16,
              loading: isLoadingWorkingSession,
              design: 'GlobalCont1',
            }}
          >
            <div className="flex-column gap-16">
              {/* No mention of greenroom button in AC for BDEV-1849 so leaving in for now */}
              {/* <Container className="flex flex-1 ai-center jc-flex-end gap-16">
                <div>
                  <SessionActionItems session={session} />
                </div>
              </Container> */}

              <Container>
                <SessionStreamInfo syncProjectDates>
                  {({ isFocusedWithin, setIsFocusedWithin }) => (
                    <div className={`margin-t-8 video-settings`}>
                      <VideoSettings setIsFocusedWithin={setIsFocusedWithin} isEditingInfo={isFocusedWithin} />
                    </div>
                  )}
                </SessionStreamInfo>
              </Container>
            </div>

            <div className="flex-column gap-24 margin-t-12">
              <div className="flex gap-20">
                <div className="flex-1 max-width-278">
                  <OptionalComponent display={seriesRequired}>
                    <EditProjectSeries />
                  </OptionalComponent>
                  <OptionalComponent display={!seriesRequired}>
                    <EditProjectCategory syncSessionUuid={sessionUuid} />
                  </OptionalComponent>
                </div>
                <div className="flex-1">
                  <EditTags sessionUuid={sessionUuid} />
                </div>
              </div>

              <EditSessionDescription sessionUuid={sessionUuid} syncProjectDescription />

              <Container width="570px" maxWidth="570px">
                <CastAndCrewSelectWithInvites sessionUuid={sessionUuid} design="flat" />
              </Container>

              <Container className="flex fd-row gap-20">
                <Container width="278px">
                  <EditSessionLanguage sessionUuid={sessionUuid} />
                </Container>

                <EditSessionCaptions />
              </Container>
            </div>
          </Container>
        </Container>

        <Container
          /*
            since we do not know if showboards exist until after the session loads, the default will be to
            show a loading indicator for the show board until sesssion loading is finished or we know the
          */
          height={isLoadingWorkingSession || session?.broadcast_type === EBroadcastTypes.greenroom ? '235px' : '0'}
          className={session?.broadcast_type === EBroadcastTypes.greenroom ? '' : 'overflow-hidden pointer-events-none'}
          skLoader={{
            height: 179,
            width: 992,
            radius: 16,
            loading: isLoadingWorkingSession,
            design: 'GlobalCont1',
          }}
        >
          <OptionalComponent display={session?.broadcast_type === EBroadcastTypes.greenroom}>
            <ResourcePoolContext.Provider value={resourcePool}>
              <GreenroomShotsRow sessionUuid={session?.uuid} />
            </ResourcePoolContext.Provider>
          </OptionalComponent>
        </Container>

        <Container
          skLoader={{
            height: 179,
            width: 992,
            radius: 16,
            loading: isLoadingWorkingSession,
            design: 'GlobalCont1',
          }}
        >
          <Engagement />
        </Container>

        <StylepacksRow />
      </div>
    </div>
  );
};

export default OverviewSingleSession;
